import { css, cx } from "@emotion/css";
import { borderRadius, fontWeight, space, themeTokens } from "@octopusdeploy/design-system-tokens";
import { useOctopusLinkComponent, type DesignSystemLinkHref } from "../../routing";
import type { ProgressTrackerItemState, ProgressTrackerLayoutOptions } from "./ProgressTracker";
export interface ProgressTrackerItemProps {
    label: string;
    state: ProgressTrackerItemState;
    href?: DesignSystemLinkHref;
    onClick?: () => void;
    layout?: ProgressTrackerLayoutOptions;
}
export const ProgressTrackerItem = ({ label, state, href, onClick, layout = "normal" }: ProgressTrackerItemProps): JSX.Element => {
    const Link = useOctopusLinkComponent();
    const itemContent = (<>
            <ProgressTrackerIcon state={state}/>
            <span>{label}</span>
        </>);
    const itemStyles = cx(styles.container(layout), {
        [styles.active]: state === "Current",
        [styles.complete]: state === "Done",
        [styles.isLink]: !!href && state === "Current",
    });
    if (state === "Current" && (href || onClick)) {
        if (href) {
            return (<Link className={itemStyles} href={href} onClick={onClick}>
                    {itemContent}
                </Link>);
        }
        return (<button className={itemStyles} onClick={onClick} type="button">
                {itemContent}
            </button>);
    }
    return <div className={itemStyles}>{itemContent}</div>;
};
const ProgressTrackerIcon = ({ state }: {
    state: ProgressTrackerItemState;
}): JSX.Element => {
    if (state === "Current") {
        return (<svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path fillRule="evenodd" clipRule="evenodd" d="M18 10C18 14.4183 14.4183 18 10 18C5.58172 18 2 14.4183 2 10C2 5.58172 5.58172 2 10 2C14.4183 2 18 5.58172 18 10Z" fill={themeTokens.color.background.success}/>
                <path d="M14 10C14 12.2091 12.2091 14 10 14C7.79086 14 6 12.2091 6 10C6 7.79086 7.79086 6 10 6C12.2091 6 14 7.79086 14 10Z" fill={themeTokens.color.icon.success}/>
            </svg>);
    }
    if (state === "Pending") {
        return (<svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="10" cy="10" r="4" fill={themeTokens.color.icon.tertiary}/>
            </svg>);
    }
    if (state === "Done") {
        return (<svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M10.0156 18C7.14062 18 4.51562 16.5 3.07812 14C1.64062 11.5312 1.64062 8.5 3.07812 6C4.51562 3.53125 7.14062 2 10.0156 2C12.8594 2 15.4844 3.53125 16.9219 6C18.3594 8.5 18.3594 11.5312 16.9219 14C15.4844 16.5 12.8594 18 10.0156 18ZM13.5469 8.53125H13.5156C13.8281 8.25 13.8281 7.78125 13.5156 7.46875C13.2344 7.1875 12.7656 7.1875 12.4844 7.46875L9.01562 10.9688L7.54688 9.5C7.23438 9.1875 6.76562 9.1875 6.48438 9.5C6.17188 9.78125 6.17188 10.25 6.48438 10.5312L8.48438 12.5312C8.76562 12.8438 9.23438 12.8438 9.54688 12.5312L13.5469 8.53125Z" fill={themeTokens.color.icon.success}/>
            </svg>);
    }
    return <></>;
};
export function getProgressTrackerItemState(index: number, activeIndex: number): ProgressTrackerItemState {
    if (index < activeIndex)
        return "Done";
    if (index > activeIndex)
        return "Pending";
    return "Current";
}
const styles = {
    container: (layout: "normal" | "compact") => css({
        display: "flex",
        alignItems: "center",
        justifyContent: "start",
        gap: layout === "compact" ? space[4] : space[8],
        borderRadius: borderRadius.small,
        whiteSpace: "nowrap",
        color: themeTokens.color.text.tertiary,
        padding: layout === "compact" ? `${space[4]} ${space[6]} ${space[4]} ${space[2]}` : `${space[4]} ${space[8]}`,
        background: "none",
        border: "none",
        font: "inherit",
        textDecoration: "none",
    }),
    active: css({
        background: themeTokens.color.background.primary.default,
        fontWeight: fontWeight[500],
        "&&": {
            color: themeTokens.color.text.primary,
        },
    }),
    complete: css({
        "&&": {
            color: themeTokens.color.text.primary,
        },
    }),
    isLink: css({
        cursor: "pointer",
        "&&:hover": {
            color: themeTokens.color.text.primary,
            background: themeTokens.color.background.primary.pressed,
        },
    }),
};
